import { useState, useEffect } from "react";
import axios from "axios";
import { 
  FileText, Search, RefreshCw, Trash2, Filter, User, Calendar,
  LogIn, LogOut, Edit2, Plus, Eye, Download, AlertTriangle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useLanguage } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_ICONS = {
  login: LogIn,
  logout: LogOut,
  create: Plus,
  update: Edit2,
  delete: Trash2,
  view: Eye,
  restore: RefreshCw
};

const ACTION_COLORS = {
  login: "text-lime-600 bg-lime-50",
  logout: "text-stone-600 bg-stone-50",
  create: "text-blue-600 bg-blue-50",
  update: "text-amber-600 bg-amber-50",
  delete: "text-rose-600 bg-rose-50",
  view: "text-purple-600 bg-purple-50",
  restore: "text-cyan-600 bg-cyan-50"
};

export const AuditLogs = () => {
  const { t } = useLanguage();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 0 });
  
  // Filters
  const [filters, setFilters] = useState({
    user_id: "",
    action: "",
    resource_type: "",
    start_date: "",
    end_date: ""
  });

  useEffect(() => {
    fetchLogs();
    fetchUsers();
  }, []);

  const fetchLogs = async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (filters.user_id) params.append("user_id", filters.user_id);
      if (filters.action) params.append("action", filters.action);
      if (filters.resource_type) params.append("resource_type", filters.resource_type);
      if (filters.start_date) params.append("start_date", filters.start_date);
      if (filters.end_date) params.append("end_date", filters.end_date);
      
      const response = await axios.get(`${API}/admin/audit-logs?${params}`, { withCredentials: true });
      setLogs(response.data.logs);
      setPagination({
        page: response.data.page,
        total: response.data.total,
        total_pages: response.data.total_pages
      });
    } catch (error) {
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/audit-logs/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm("Are you sure you want to delete logs older than 30 days?")) return;
    
    try {
      const response = await axios.delete(`${API}/admin/audit-logs/cleanup`, { withCredentials: true });
      toast.success(response.data.message);
      fetchLogs();
    } catch (error) {
      toast.error("Failed to cleanup logs");
    }
  };

  const applyFilters = () => {
    fetchLogs(1);
  };

  const clearFilters = () => {
    setFilters({
      user_id: "",
      action: "",
      resource_type: "",
      start_date: "",
      end_date: ""
    });
    setTimeout(() => fetchLogs(1), 100);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getActionIcon = (action) => {
    const IconComponent = ACTION_ICONS[action] || FileText;
    return IconComponent;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-outfit font-semibold text-stone-900 flex items-center gap-2">
                <FileText className="w-5 h-5 text-lime-700" />
                User Activity Logs
              </CardTitle>
              <CardDescription>Track all user actions and system changes (30 day retention)</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => fetchLogs(pagination.page)}
                variant="outline"
                size="sm"
                data-testid="refresh-logs-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button
                onClick={handleCleanup}
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                data-testid="cleanup-logs-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Cleanup Old Logs
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="mb-6 p-4 bg-stone-50 rounded-lg border border-stone-200">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-stone-500" />
              <span className="font-medium text-stone-700">Filters</span>
            </div>
            <div className="grid grid-cols-5 gap-4">
              <div>
                <Label className="text-xs">User</Label>
                <Select value={filters.user_id} onValueChange={(v) => setFilters({...filters, user_id: v === "all" ? "" : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user} value={user}>{user}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Action</Label>
                <Select value={filters.action} onValueChange={(v) => setFilters({...filters, action: v === "all" ? "" : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Actions" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Actions</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="logout">Logout</SelectItem>
                    <SelectItem value="create">Create</SelectItem>
                    <SelectItem value="update">Update</SelectItem>
                    <SelectItem value="delete">Delete</SelectItem>
                    <SelectItem value="restore">Restore</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Resource Type</Label>
                <Select value={filters.resource_type} onValueChange={(v) => setFilters({...filters, resource_type: v === "all" ? "" : v})}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="All Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="session">Session</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="transaction">Transaction</SelectItem>
                    <SelectItem value="manufacturing_batch">Manufacturing</SelectItem>
                    <SelectItem value="warehouse">Warehouse</SelectItem>
                    <SelectItem value="backup">Backup</SelectItem>
                    <SelectItem value="user">User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Start Date</Label>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">End Date</Label>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                  className="mt-1"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <Button onClick={applyFilters} size="sm" className="bg-lime-700 text-white hover:bg-lime-800">
                <Search className="w-4 h-4 mr-2" />
                Apply Filters
              </Button>
              <Button onClick={clearFilters} size="sm" variant="outline">
                Clear Filters
              </Button>
            </div>
          </div>

          {/* Logs Table */}
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-stone-50">
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Warehouse</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-stone-500">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    const colorClass = ACTION_COLORS[log.action] || "text-stone-600 bg-stone-50";
                    
                    return (
                      <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                        <TableCell className="text-xs text-stone-600 font-mono">
                          {formatTimestamp(log.timestamp)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-3 h-3 text-stone-400" />
                            <span className="font-medium">{log.user_name}</span>
                            <span className="text-xs text-stone-400 capitalize">({log.user_role})</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${colorClass}`}>
                            <ActionIcon className="w-3 h-3" />
                            {log.action}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="font-medium capitalize">{log.resource_type?.replace('_', ' ')}</span>
                            {log.resource_name && (
                              <span className="text-stone-500 ml-1">: {log.resource_name}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          {log.details ? (
                            <span className="text-xs text-stone-500 truncate block" title={log.details}>
                              {log.details.length > 50 ? log.details.substring(0, 50) + '...' : log.details}
                            </span>
                          ) : (
                            <span className="text-stone-300">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-stone-600">
                          {log.warehouse || "-"}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-stone-500">
                Showing page {pagination.page} of {pagination.total_pages} ({pagination.total} total logs)
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => fetchLogs(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <Button
                  onClick={() => fetchLogs(pagination.page + 1)}
                  disabled={pagination.page >= pagination.total_pages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogs;
